import React, { useState, useEffect } from "react";
import { collection, getDocs, doc, updateDoc, deleteDoc, query, orderBy } from "firebase/firestore";
import { db, applicationsCol } from "../firebase";
import { useUser } from "../UserContext";
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import AdminNavbar from './AdminNavbar';
import ApplicationList from './ApplicationList';
import ApplicationDetail from './ApplicationDetail';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { user, loading } = useUser();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [levelFilter, setLevelFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [applicationsPerPage] = useState(10);
  const [showDetail, setShowDetail] = useState(false);

  // Check if user is admin (you can modify this logic based on your admin identification)
  const isAdmin = user?.email === 'athumanzathuman@gmail.com';

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    } else if (!loading && user && !isAdmin) {
      navigate('/');
    }
  }, [user, loading, navigate, isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      fetchApplications();
    }
  }, [isAdmin]);

  useEffect(() => {
    filterApplications();
  }, [applications, searchTerm, statusFilter, levelFilter]);

  const fetchApplications = async () => {
    try {
      setLoadingApplications(true);
      const applicationsRef = collection(db, applicationsCol);
      const q = query(applicationsRef, orderBy('submittedAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const applicationsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setApplications(applicationsData);
    } catch (error) {
      console.error("Error fetching applications:", error);
    } finally {
      setLoadingApplications(false);
    }
  };

  const filterApplications = () => {
    let filtered = applications;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(app => 
        app.applicantFirstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.applicantLastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.applicantEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.firstChoice?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter(app => app.status === statusFilter);
    }

    // Filter by level
    if (levelFilter !== "all") {
      filtered = filtered.filter(app => app.level === levelFilter);
    }

    setFilteredApplications(filtered);
    setCurrentPage(1);
  };

  const handleStatusUpdate = async (applicationId, newStatus, selectedCourse = null) => {
    try {
      const applicationRef = doc(db, applicationsCol, applicationId);
      const updateData = {
        status: newStatus,
        updatedAt: new Date(),
        updatedBy: user.email
      };
      
      // Add selected course if status is "selected"
      if (newStatus === "selected" && selectedCourse) {
        updateData.selectedCourse = selectedCourse;
      }
      
      await updateDoc(applicationRef, updateData);
      
      // Update local state
      setApplications(prev => 
        prev.map(app => 
          app.id === applicationId 
            ? { 
                ...app, 
                status: newStatus, 
                updatedAt: new Date(), 
                updatedBy: user.email,
                ...(newStatus === "selected" && selectedCourse && { selectedCourse })
              }
            : app
        )
      );
      
      if (selectedApplication?.id === applicationId) {
        setSelectedApplication(prev => ({
          ...prev,
          status: newStatus,
          updatedAt: new Date(),
          updatedBy: user.email,
          ...(newStatus === "selected" && selectedCourse && { selectedCourse })
        }));
      }
    } catch (error) {
      console.error("Error updating application status:", error);
      alert("Failed to update application status");
    }
  };

  const handleDeleteApplication = async (applicationId) => {
    if (window.confirm("Are you sure you want to delete this application? This action cannot be undone.")) {
      try {
        await deleteDoc(doc(db, applicationsCol, applicationId));
        
        // Update local state
        setApplications(prev => prev.filter(app => app.id !== applicationId));
        
        if (selectedApplication?.id === applicationId) {
          setSelectedApplication(null);
          setShowDetail(false);
        }
        
        alert("Application deleted successfully");
      } catch (error) {
        console.error("Error deleting application:", error);
        alert("Failed to delete application");
      }
    }
  };

  const handleViewDetail = (application) => {
    setSelectedApplication(application);
    setShowDetail(true);
  };

  const handleLogout = async () => {
    await auth.signOut();
    navigate('/login');
  };

  if (loading) return <div className="admin-loading">Loading...</div>;
  if (!isAdmin) return null;

  // Pagination
  const indexOfLastApplication = currentPage * applicationsPerPage;
  const indexOfFirstApplication = indexOfLastApplication - applicationsPerPage;
  const currentApplications = filteredApplications.slice(indexOfFirstApplication, indexOfLastApplication);
  const totalPages = Math.ceil(filteredApplications.length / applicationsPerPage);

  return (
    <div className="admin-dashboard">
      <AdminNavbar 
        onLogout={handleLogout}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        levelFilter={levelFilter}
        setLevelFilter={setLevelFilter}
        totalApplications={applications.length}
        filteredApplications={filteredApplications.length}
      />
      
      <div className="admin-content">
        {showDetail && selectedApplication ? (
          <ApplicationDetail
            application={selectedApplication}
            onBack={() => setShowDetail(false)}
            onStatusUpdate={handleStatusUpdate}
            onDelete={handleDeleteApplication}
          />
        ) : (
          <ApplicationList
            applications={currentApplications}
            loading={loadingApplications}
            onViewDetail={handleViewDetail}
            onStatusUpdate={handleStatusUpdate}
            onDelete={handleDeleteApplication}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard; 